<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] sm:flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('ecommerce.orderDetails.productsInOrder') }}</h5>
      </div>
      <div class="trezo-card-subtitle mt-[15px] sm:mt-0" v-if="order">
        <h5 class="mb-0" v-if="order.customer_id">
          {{ directT('ecommerce.orderDetails.customerID') }}: {{ order.customer_id }}
        </h5>
        <span v-if="!order.customer_id && order.name" class="text-sm text-gray-500">
          {{ directT('ecommerce.orderDetails.customer') }}: {{ order.name }}
        </span>
      </div>
    </div>

    <div class="trezo-card-content">
      <div v-if="loading" class="text-center py-4">
        <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
      </div>
      <div v-else-if="!orderProducts || orderProducts.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400">
        {{ directT('ecommerce.orderDetails.noItemsFound') || 'No items found in this order' }}
      </div>
      <div v-else class="table-responsive overflow-x-auto">
        <table class="w-full">
          <thead class="text-black dark:text-white">
            <tr>
              <th
                class="font-medium ltr:text-left rtl:text-right px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tl-md"
              >
                {{ directT('ecommerce.orderDetails.product') }}
              </th>
              <th
                class="font-medium ltr:text-center rtl:text-center px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('ecommerce.orderDetails.quantity') }}
              </th>
              <th
                class="font-medium ltr:text-right rtl:text-left px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap"
              >
                {{ directT('ecommerce.orderDetails.price') }}
              </th>
              <th
                class="font-medium ltr:text-right rtl:text-left px-[20px] py-[11px] bg-gray-50 dark:bg-[#15203c] whitespace-nowrap first:rounded-tr-md"
              >
                {{ directT('ecommerce.orderDetails.total') }}
              </th>
            </tr>
          </thead>
          <tbody class="text-black dark:text-white">
            <tr v-for="(product, index) in orderProducts" :key="product.id || index">
              <td
                class="ltr:text-left rtl:text-right px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036] ltr:first:border-l ltr:last:border-r rtl:first:border-r rtl:last:border-l"
              >
                <div class="flex items-center">
                  <div class="rounded-md w-[40px] h-[40px] overflow-hidden flex-shrink-0">
                    <img
                      :src="product.image || getFallbackProductImage(index)"
                      class="inline-block rounded-md object-cover w-full h-full"
                      alt="product-image"
                      @error="onImageError($event, index)"
                    />
                  </div>
                  <div class="ltr:ml-[12px] rtl:mr-[12px] flex-grow min-w-0">
                    <span class="block font-medium md:text-[15px] truncate">
                      {{ product.name || `Product ${index + 1}` }}
                    </span>
                    <span v-if="product.description" class="text-xs text-gray-500 dark:text-gray-400 mt-1 block truncate">
                      {{ truncateText(product.description, 30) }}
                    </span>
                    <span v-if="product.id" class="text-xs text-gray-400 mt-1 block">
                      ID: {{ truncateText(product.id, 10) }}
                    </span>
                  </div>
                </div>
              </td>
              <td
                class="ltr:text-center rtl:text-center whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036]"
              >
                {{ product.quantity || 1 }}
              </td>
              <td
                class="ltr:text-right rtl:text-left whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036]"
              >
                {{ formatPrice(product.price) }}
              </td>
              <td
                class="ltr:text-right rtl:text-left whitespace-nowrap px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036]"
              >
                {{ formatPrice(product.price * (product.quantity || 1)) }}
              </td>
            </tr>
            <tr v-if="orderProducts.length > 0" class="bg-gray-50 dark:bg-[#15203c]">
              <td colspan="3" class="ltr:text-right rtl:text-left font-medium px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036]">
                {{ directT('ecommerce.orderDetails.total') }}:
              </td>
              <td class="ltr:text-right rtl:text-left font-medium px-[20px] py-[15px] border-b border-gray-100 dark:border-[#172036]">
                {{ formatPrice(calculateTotal()) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, onMounted, ref } from 'vue';
import { useI18n } from "vue-i18n";
import enTranslations from "@/i18n/locales/en.json";
import heTranslations from "@/i18n/locales/he.json";
import arTranslations from "@/i18n/locales/ar.json";
import { Order } from "@/services/orderService";

// Type definition for translation objects
type TranslationObject = {
  [key: string]: string | TranslationObject;
};

export default defineComponent({
  name: "RecentOrders",
  props: {
    orderId: {
      type: String,
      required: false,
      default: ''
    },
    order: {
      type: Object as PropType<Order | null>,
      required: false,
      default: null
    }
  },
  setup(props) {
    // i18n setup
    const { locale } = useI18n();
    const loading = ref(false);
    
    // Translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };
    
    // Access order products with fallback to empty array
    const orderProducts = computed(() => {
      if (!props.order?.products) return [];
      
      const products = props.order.products;
      console.log('Order products in component:', products);
      
      // Make sure we return an array
      return Array.isArray(products) ? products : [];
    });
    
    // Calculate total from products
    const calculateTotal = () => {
      return orderProducts.value.reduce((total, product) => {
        const price = parseFloat(String(product.price || 0));
        const quantity = parseInt(String(product.quantity || 1), 10);
        return total + (price * quantity);
      }, 0);
    };
    
    // Format price
    const formatPrice = (price: any): string => {
      if (price === undefined || price === null) return '$0.00';
      
      const numPrice = parseFloat(String(price));
      
      if (isNaN(numPrice)) return '$0.00';
      
      return locale.value === 'he' || locale.value === 'ar'
        ? `₪${numPrice.toFixed(2)}`
        : `$${numPrice.toFixed(2)}`;
    };
    
    // Truncate text for descriptions
    const truncateText = (text: string, maxLength: number): string => {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };
    
    // Handle image loading errors
    const onImageError = (event: Event, index: number) => {
      const target = event.target as HTMLImageElement;
      target.src = getFallbackProductImage(index);
    };
    
    // Fallback product image based on index
    const getFallbackProductImage = (index: number) => {
      const imageNumber = (index % 5) + 1;
      return new URL(`../../../../assets/images/products/product${imageNumber}.jpg`, import.meta.url).href;
    };
    
    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    // Log order data on mount to check what we're working with
    onMounted(() => {
      loading.value = true;
      console.log('Order data:', props.order);
      console.log('Order products:', props.order?.products);
      
      // Simulate loading time to prevent flickering
      setTimeout(() => {
        loading.value = false;
      }, 500);
    });
    
    return {
      directT,
      orderProducts,
      formatPrice,
      getFallbackProductImage,
      truncateText,
      onImageError,
      calculateTotal,
      loading
    };
  }
});
</script>
